/**
 * P0-11 · Class-name + variant helpers. Dependency-free (no clsx/cva/
 * tailwind-merge available). `cn` joins truthy classes; `cva` builds a
 * token-driven variant function. Component usage is controlled, so we don't need
 * tailwind-merge conflict resolution.
 */
export type ClassValue = string | false | null | undefined;

export function cn(...classes: ClassValue[]): string {
  return classes.filter(Boolean).join(" ");
}

type VariantConfig<V extends Record<string, Record<string, string>>> = {
  base?: string;
  variants: V;
  defaultVariants?: { [K in keyof V]?: keyof V[K] };
};

export type VariantProps<F> = F extends (props: infer P) => string ? P : never;

/**
 * Minimal `cva`: returns a function mapping selected variant keys → a class
 * string. Unspecified props fall back to `defaultVariants`.
 */
export function cva<V extends Record<string, Record<string, string>>>(
  config: VariantConfig<V>,
) {
  return (
    props: { [K in keyof V]?: keyof V[K] } & { className?: ClassValue } = {},
  ): string => {
    const parts: ClassValue[] = [config.base];
    (Object.keys(config.variants) as (keyof V)[]).forEach((key) => {
      const group = config.variants[key];
      if (!group) return;
      const selected = props[key] ?? config.defaultVariants?.[key];
      if (selected != null) {
        const cls = group[selected as string];
        if (cls) parts.push(cls);
      }
    });
    parts.push(props.className);
    return cn(...parts);
  };
}
